import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const FetchQueueContext = createContext(null);

const CACHE_TTL = 30000; // 30 seconds default cache TTL

export const FetchQueueProvider = ({ children, layoutOrder = [] }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const queueRef = useRef(new Map());
    const cacheRef = useRef(new Map());
    const layoutOrderRef = useRef(layoutOrder);
    const processingRef = useRef(false);

    // Update layout order ref when prop changes
    useEffect(() => {
        layoutOrderRef.current = layoutOrder;
    }, [layoutOrder]);

    // Check if cache entry is valid
    const isCacheValid = useCallback((cacheKey) => {
        if (!cacheRef.current.has(cacheKey)) return false;
        const entry = cacheRef.current.get(cacheKey);
        return Date.now() - entry.timestamp < (entry.ttl || CACHE_TTL);
    }, []);

    // Get cached data
    const getCachedData = useCallback((cacheKey) => {
        if (!isCacheValid(cacheKey)) return null;
        return cacheRef.current.get(cacheKey).data;
    }, [isCacheValid]);

    // Set cache data
    const setCacheData = useCallback((cacheKey, data, ttl = CACHE_TTL) => {
        cacheRef.current.set(cacheKey, {
            data,
            timestamp: Date.now(),
            ttl,
        });
    }, []);

    // Clear cache for a specific key
    const clearCache = useCallback((cacheKey) => {
        cacheRef.current.delete(cacheKey);
    }, []);

    // Process the queue sequentially based on layout order
    const processQueue = useCallback(async () => {
        // Prevent concurrent processing
        if (processingRef.current || queueRef.current.size === 0) {
            return;
        }

        processingRef.current = true;
        setIsProcessing(true);

        try {
            // Find the next widget to process based on layout order
            let nextWidgetId = null;

            // First, try to find a widget in layout order
            for (const widgetId of layoutOrderRef.current) {
                if (queueRef.current.has(widgetId)) {
                    nextWidgetId = widgetId;
                    break;
                }
            }

            // If no widget found in layout order, take the first one in queue
            if (!nextWidgetId && queueRef.current.size > 0) {
                nextWidgetId = queueRef.current.keys().next().value;
            }

            if (!nextWidgetId) {
                processingRef.current = false;
                setIsProcessing(false);
                return;
            }

            const fetchConfig = queueRef.current.get(nextWidgetId);
            if (!fetchConfig) {
                queueRef.current.delete(nextWidgetId);
                processingRef.current = false;
                setIsProcessing(false);
                processQueue();
                return;
            }

            const { apiCall, cacheKey, cacheTtl, resolve, reject } = fetchConfig;

            // Check cache first
            if (cacheKey && isCacheValid(cacheKey)) {
                const cachedData = getCachedData(cacheKey);
                resolve(cachedData);
                queueRef.current.delete(nextWidgetId);
                processingRef.current = false;
                setIsProcessing(false);
                // Process next item
                setTimeout(() => processQueue(), 0);
                return;
            }

            // Execute the API call
            try {
                const response = await apiCall();
                const data = response.data;

                // Cache the result if cacheKey provided
                if (cacheKey) {
                    setCacheData(cacheKey, data, cacheTtl);
                }

                resolve(data);
            } catch (error) {
                reject(error);
            }

            // Remove from queue and process next
            queueRef.current.delete(nextWidgetId);
            processingRef.current = false;
            setIsProcessing(false);

            // Process next item in queue
            if (queueRef.current.size > 0) {
                setTimeout(() => processQueue(), 0);
            }
        } catch (error) {
            processingRef.current = false;
            setIsProcessing(false);
        }
    }, [isCacheValid, getCachedData, setCacheData]);

    // Register a fetch request from a widget
    const registerFetch = useCallback((widgetId, fetchConfig) => {
        return new Promise((resolve, reject) => {
            // Check cache first before even queuing
            const { cacheKey } = fetchConfig;
            if (cacheKey && isCacheValid(cacheKey)) {
                resolve(getCachedData(cacheKey));
                return;
            }

            // Add to queue with promise handlers
            queueRef.current.set(widgetId, {
                ...fetchConfig,
                resolve,
                reject,
            });

            // Start processing if not already
            processQueue();
        });
    }, [isCacheValid, getCachedData, processQueue]);

    // Unregister a widget (cleanup on unmount)
    const unregisterWidget = useCallback((widgetId) => {
        const fetchConfig = queueRef.current.get(widgetId);
        if (fetchConfig) {
            // Reject with cancellation error
            fetchConfig.reject(new Error('Widget unmounted'));
            queueRef.current.delete(widgetId);
        }
    }, []);

    // Force refresh for a widget (for polling)
    const queueRefresh = useCallback((widgetId, fetchConfig) => {
        // Clear any existing cache for this widget's data
        if (fetchConfig.cacheKey) {
            clearCache(fetchConfig.cacheKey);
        }

        return registerFetch(widgetId, fetchConfig);
    }, [registerFetch, clearCache]);

    const patchCache = useCallback((cacheKey, data) => {
        const existing = cacheRef.current.get(cacheKey);
        setCacheData(cacheKey, data, existing?.ttl);
    }, [setCacheData]);

    const value = {
        registerFetch,
        unregisterWidget,
        queueRefresh,
        clearCache,
        patchCache,
        isProcessing,
    };

    return (
        <FetchQueueContext.Provider value={value}>
            {children}
        </FetchQueueContext.Provider>
    );
};

export const useFetchQueueContext = () => {
    const context = useContext(FetchQueueContext);
    if (!context) {
        throw new Error('useFetchQueueContext must be used within a FetchQueueProvider');
    }
    return context;
};

export default FetchQueueContext;
